import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import { SvgIcon, useMediaQuery, useTheme } from '@mui/material'
import { ReactComponent as CloseIcon } from '../../../assets/images/Close.svg'
import { useGetTPPColumnVersionQuery } from '../queries/useGetTPPColumnVersionQuery'
import { useTppData } from '../../TppView/hooks/useTppData'
import VersionPreviewContent from './VersionPreviewContent'
import { formatDate } from '../utils/formatters'

interface VersionPreviewModalProps {
    versionId: string | null
    tppId: string
    onClose: () => void
}

const VersionPreviewModal = ({ versionId, tppId, onClose }: VersionPreviewModalProps) => {
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const { data: version, isLoading: versionLoading, isError: versionError } = useGetTPPColumnVersionQuery(versionId)
    const { matrixState, isLoading: matrixLoading } = useTppData(tppId)

    const isLoading = versionLoading || matrixLoading
    const isOpen = !!versionId

    const formattedDate = version?.createdAt ? formatDate(version.createdAt) : ''
    const userName = version?.createdBy?.name || 'Unknown user'

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth
            fullScreen={isSmallScreen}
            maxWidth="md"
            aria-labelledby="version-preview-dialog-title"
            PaperProps={{
                sx: {
                    py: { xs: 2, sm: 2, md: 3 },
                    px: { xs: 2, sm: 3, md: 4 },
                    borderRadius: { xs: 0, sm: '24px' },
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                    height: { xs: '100%', sm: '80vh', md: '75vh' },
                    maxHeight: { xs: 'none', sm: '700px' },
                },
            }}
        >
            <DialogTitle id="version-preview-dialog-title" sx={{ p: 0, pb: 2 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Stack gap={0.5}>
                        <Typography variant="18x600" color="figma.grayscale.textHier1">
                            Version: {version?.versionNumber ?? ''}
                        </Typography>
                        {version && (
                            <Typography variant="14x400" color="figma.grayscale.textHier2">
                                Saved: {formattedDate}, by {userName}
                            </Typography>
                        )}
                    </Stack>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {isLoading && (
                    <Stack flex={1} alignItems="center" justifyContent="center">
                        <CircularProgress size={40} />
                    </Stack>
                )}

                {versionError && (
                    <Stack flex={1} alignItems="center" justifyContent="center">
                        <Typography variant="16x400" color="error">
                            Error loading version. Please try again.
                        </Typography>
                    </Stack>
                )}

                {!isLoading && !versionError && version && matrixState && (
                    <VersionPreviewContent version={version} matrixState={matrixState} />
                )}
            </DialogContent>
        </Dialog>
    )
}

export default VersionPreviewModal
